import { Box, Flex, HStack, Stack, Text, useDisclosure } from '@chakra-ui/react';
import { IndividualRow } from 'app/components/UI/Table/Table.types';
import { UseFormWatch } from 'react-hook-form';
import { PointsTransactionReasonLabels } from 'app/constants/pointsTransactions';

import { EyeOutline } from '@aslanpay/portal-common-lib';

import formatNumber from 'app/utils/formatting';

import { useState } from 'react';
import { PaginationState } from '@tanstack/react-table';

import { formatScheduledAt, toScheduledAt } from 'app/utils/scheduledGifts';

import BulkGiftingDetailsModal from '../BulkGiftingDetailsModal';

import { RewardFormFields } from './types';

interface RewardConfirmAndSendFormProps {
  selectedEmployeeIds: string[];
  watch: UseFormWatch<RewardFormFields>;
  employeeData: IndividualRow[];
  /** The gift is scheduled for later rather than sent now. */
  isScheduling?: boolean;
  /** Also posted as a recognition; only shown when scheduling, the only time it applies. */
  postAsRecognition?: boolean;
  /**
   * Set when the employees of a gift being edited can't change: why, shown in place of the list. The count then
   * comes from the gift itself.
   */
  lockedRecipients?: { count: number; message: string };
}

function RewardConfirmAndSendForm({
  selectedEmployeeIds,
  watch,
  employeeData,
  isScheduling = false,
  postAsRecognition = false,
  lockedRecipients = undefined,
}: RewardConfirmAndSendFormProps) {
  const pointsAmount = watch('pointsAmount');
  const reason = watch('transactionReason');
  const customMessage = watch('customMessage');
  const employeeCount = lockedRecipients?.count ?? selectedEmployeeIds.length;
  const totalPoints = employeeCount * pointsAmount;
  const sendTime = isScheduling
    ? formatScheduledAt(toScheduledAt(watch('scheduleDate'), watch('scheduleTime')) || '')
    : 'Now';

  const [employeesPagination, setEmployeesPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 15,
  });

  const {
    isOpen: isBulkGiftingDetailsOpen,
    onOpen: onOpenBulkGiftingDetails,
    onClose: onCloseBulkGiftingDetails,
  } = useDisclosure();

  const allEmployeesToShow = employeeData
    .filter((emp) => selectedEmployeeIds.includes(emp.id))
    .map((emp) => ({
      fullName: `${emp.firstName} ${emp.lastName}`,
      email: emp.email,
      points: pointsAmount,
    }))
    .sort((a, b) => a.fullName.localeCompare(b.fullName));

  const employeesToShow = allEmployeesToShow.slice(
    employeesPagination.pageIndex * employeesPagination.pageSize,
    (employeesPagination.pageIndex + 1) * employeesPagination.pageSize
  );

  const reasonLabel = Object.prototype.hasOwnProperty.call(PointsTransactionReasonLabels, reason)
    ? PointsTransactionReasonLabels[reason as keyof typeof PointsTransactionReasonLabels].reason
    : reason || '';

  const onCloseDetailsModal = () => {
    setEmployeesPagination({ pageIndex: 0, pageSize: 15 });
    onCloseBulkGiftingDetails();
  };

  return (
    <Flex justify="center">
      <Box
        bg="brand.lightGray2"
        borderRadius="lg"
        p={8}
        maxW="600px"
        width="100%"
        borderColor="brand.lightGray1"
        borderWidth={1}
      >
        <Stack spacing={6}>
          <Box borderRadius="md">
            <Text textStyle="smallRegular" color="brand.darkGray" mb="6px">
              No. of employees to reward:
            </Text>
            <HStack mt="6px">
              <Text
                textStyle="h4Medium"
                color="brand.mutedPurple"
                mr="16px"
                data-testid="confirm-employee-count"
              >
                {employeeCount}
              </Text>
              {!lockedRecipients && (
                <EyeOutline
                  boxSize={6}
                  onClick={onOpenBulkGiftingDetails}
                  scale={1.5}
                  color="brand.purple"
                  _hover={{ cursor: 'pointer' }}
                />
              )}
            </HStack>
            {lockedRecipients && (
              <Text textStyle="smallRegular" color="brand.darkGray" mt="6px">
                {lockedRecipients.message}
              </Text>
            )}
          </Box>
          <Box borderRadius="md" mt="20px">
            <Text textStyle="smallRegular" color="brand.darkGray" mb="6px">
              Amount of points
            </Text>
            <Text textStyle="h4Medium" color="brand.mutedPurple">
              {formatNumber(totalPoints)}
            </Text>
          </Box>
          <Box borderRadius="md" mt="20px">
            <Text textStyle="smallRegular" color="brand.darkGray" mb="6px">
              Reason
            </Text>
            <Text textStyle="smallSemiBold" color="brand.mutedPurple">
              {reasonLabel}
            </Text>
          </Box>
          <Box borderRadius="md" mt="20px">
            <Text textStyle="smallRegular" color="brand.darkGray" mb="6px">
              Notification reason text
            </Text>
            <Text textStyle="smallSemiBold" color="brand.mutedPurple">
              {customMessage}
            </Text>
          </Box>
          <Box borderRadius="md" mt="20px" data-testid="confirm-send-time">
            <Text textStyle="smallRegular" color="brand.darkGray" mb="6px">
              Send
            </Text>
            <Text textStyle="smallSemiBold" color="brand.mutedPurple">
              {sendTime}
            </Text>
          </Box>
          {isScheduling && postAsRecognition && (
            <Box borderRadius="md" mt="20px">
              <Text textStyle="smallRegular" color="brand.darkGray" mb="6px">
                Also posted as a recognition
              </Text>
              <Text textStyle="smallSemiBold" color="brand.mutedPurple">
                Yes
              </Text>
            </Box>
          )}
        </Stack>
      </Box>
      <BulkGiftingDetailsModal
        isOpen={isBulkGiftingDetailsOpen}
        onClose={onCloseDetailsModal}
        employees={employeesToShow}
        pagination={employeesPagination}
        onPaginationChange={setEmployeesPagination}
        totalEmployees={allEmployeesToShow.length}
      />
    </Flex>
  );
}

export default RewardConfirmAndSendForm;
